import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { GuideDetailsOtherComponent } from './guide-details-other.component';
import { GuideDetailsStornoComponent } from './guide-details-storno.component';
import { GuideDetailsTourComponent } from './guide-details-tour.component';
import { GuideDetailsPositionDetailsComponent } from './guide-details-position-details.component';
import { GuideDetailsTourButtonsComponent } from './guide-details-tour-buttons.component';
import { SimpleCheckModalComponent } from '../../../modals/simple-check-modal.component';
import {
  GuideDetailInterface,
  TourDetailInterface,
} from '../../../../core/models';

@Component({
  selector: 'app-guide-details',
  imports: [
    CommonModule,
    SimpleCheckModalComponent,
    ReactiveFormsModule,
    GuideDetailsOtherComponent,
    GuideDetailsStornoComponent,
    GuideDetailsTourComponent,
    GuideDetailsPositionDetailsComponent,
    GuideDetailsTourButtonsComponent,
  ],
  standalone: true,
  template: `
    <div
      [formGroup]="guide"
      *ngIf="inputGuide"
      [ngClass]="{
        'bg-red-50 border-red-900 border-2 sm:border-4 rounded-xl':
          activeStorno.value,
      }"
    >
      <ul role="list" class="divide-y divide-gray-100">
        <li class="flex justify-between gap-x-6 items-center">
          <div class="flex flex-col w-full">
            <div
              class="flex flex-col lg:flex-row lg:justify-between lg:gap-x-6 pt-2 lg:items-center gap-y-4 lg:gap-y-0 w-full"
            >
              <div
                class="shrink-0 flex sm:flex-col items-start sm:items-end order-first lg:order-none"
              >
                <button
                  (click)="onDelete()"
                  type="button"
                  class="rounded-md bg-red-500 px-0.5 py-0.5 text-sm font-semibold text-white shadow-xs hover:bg-red-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="size-6"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                    />
                  </svg>
                </button>
              </div>
              <div class="flex flex-col min-w-0 gap-x-4 w-full lg:w-auto">
                <div class="min-w-0 flex-auto">
                  <p class="text-sm font-semibold leading-6 text-gray-900">
                    {{ inputGuide.forename }} {{ inputGuide.surname }}
                  </p>
                  <p class="mt-0.5 truncate text-xxs  text-gray-500">
                    {{ inputGuide.email }}
                  </p>
                  <p class="truncate text-xxs  text-gray-500">
                    {{ inputGuide.tel }}
                  </p>
                </div>
              </div>

              <div
                class="flex flex-col xl:flex-row gap-y-4 xl:gap-y-0 xl:gap-x-6 w-full lg:w-auto"
              >
                <app-guide-details-position-details
                  [positionDetails]="positionDetails"
                />

                <app-guide-details-tour
                  [tour]="tour"
                  [tourSource]="tours"
                  [languages]="languages"
                />
                <app-guide-details-tour-buttons
                  [other]="other"
                  [activeStorno]="activeStorno"
                />
              </div>
            </div>

            <div
              class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 items-start pt-4"
            >
              <div *ngIf="activeStorno.value">
                <app-guide-details-storno
                  [storno]="storno"
                  (delete)="onStornoToggle()"
                />
              </div>
              <app-guide-details-other
                *ngFor="let oth of other.controls; let i = index"
                [other]="convertAbstractToFormGroup(oth)"
                (delete)="onDeleteOther(i)"
              />
            </div>
          </div>
        </li>
      </ul>
    </div>
    <div *ngIf="deleteToggle && inputGuide">
      <app-simple-check-modal
        [message]="
          'Möchtest du Guide ' +
          inputGuide.forename +
          ' ' +
          inputGuide.surname +
          ' entfernen?'
        "
        [title]="'Guide entfernen?'"
        [positiveButtonText]="'Yes'"
        [negativeButtonText]="'No'"
        (close)="onDeleteModal($event)"
      ></app-simple-check-modal>
    </div>
  `,

  styles: ``,
})
export class GuideDetailsComponent implements OnInit {
  @Input({ required: true }) guide!: FormGroup;
  @Input({ required: true }) tours!: TourDetailInterface[];
  @Output() delete = new EventEmitter<boolean>();

  deleteToggle = false;

  inputGuide: GuideDetailInterface | undefined;

  ngOnInit(): void {
    if (this.guide instanceof FormGroup) {
      this.inputGuide = this.guideDetails?.getRawValue();
    } else {
      console.error('guide is not a FormGroup:', this.guide);
    }
  }

  convertAbstractToFormGroup(control: AbstractControl) {
    return control as FormGroup;
  }
  get activeStorno() {
    return this.storno.get('isActive') as FormControl;
  }
  get storno() {
    return this.guide.get('storno') as FormGroup;
  }

  get languages() {
    return this.guideDetails.get('languages') as FormArray;
  }
  get other() {
    return this.guide.get('other') as FormArray;
  }
  get tour() {
    return this.guide?.get('tour') as FormGroup;
  }

  get positionDetails() {
    return this.guide?.get('positionDetails') as FormGroup;
  }

  get guideDetails() {
    return this.guide?.get('guideDetails') as FormGroup;
  }

  onDeleteToggle() {
    this.deleteToggle = !this.deleteToggle;
  }

  onDelete() {
    this.onDeleteToggle();
  }

  onDeleteModal(value: boolean) {
    if (value) {
      this.delete.emit(true);
    }

    this.onDeleteToggle();
  }

  onDeleteOther(index: number) {
    this.other.removeAt(index);
  }

  onStornoToggle() {
    let val = this.activeStorno.value;
    this.activeStorno.setValue(!val);
  }
}
